import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import { GRAY } from "../../../../constants";
import { Grid } from "@material-ui/core";
import IconButton from "@material-ui/core/IconButton";
import KeyboardArrowLeft from "@material-ui/icons/KeyboardArrowLeft";
import KeyboardArrowRight from "@material-ui/icons/KeyboardArrowRight";

interface PaginationProps {
  page: number;
  changePage: (page: number) => void;
  rowsPerPage: number;
  totalRecords: number;
}

const useStyles = makeStyles(() => ({
  button: {
    marginRight: "5px",
    marginLeft: "5px",
    paddingTop: "10px",
    paddingBottom: "10px"
  },
  pageControlsWrapper: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    justifyContent: "center"
  },
  selectedButton: {
    backgroundColor: GRAY,
    marginRight: "5px",
    marginLeft: "5px",
    paddingTop: "10px",
    paddingBottom: "10px"
  }
}));

const AdhocProjectsTablePagination: React.FC<PaginationProps> = ({
  page,
  changePage,
  rowsPerPage,
  totalRecords
}) => {
  const classes = useStyles();
  const pages = Math.ceil(totalRecords / rowsPerPage);

  const handleBackButtonClick = () => {
    changePage(page - 1);
  };

  const handleNextButtonClick = () => {
    changePage(page + 1);
  };

  const renderButtons = () => {
    const maxNumberOfButtons = 9;
    const pagesArray = [];
    const ellipsis = "...";
    let counter = 1;
    if (pages <= maxNumberOfButtons) {
      while (counter <= pages) {
        pagesArray.push({ key: counter, value: counter });
        counter += 1;
      }
    } else {
      if (page < maxNumberOfButtons - Math.floor(maxNumberOfButtons / 2)) {
        // one of the first few pages e.g. [1,_2_,3,4,5,'...',19]
        counter = 1;
        while (counter <= maxNumberOfButtons - 2) {
          pagesArray.push({ key: counter, value: counter });
          counter += 1;
        }
        pagesArray.push({ key: ellipsis, value: ellipsis });
        pagesArray.push({ key: pages, value: pages });
      } else if (
        page >
        pages - maxNumberOfButtons + Math.floor(maxNumberOfButtons / 2)
      ) {
        // one of the last few pages e.g. [1,'...',15,16,17,_18_,19]
        pagesArray.push({ key: 1, value: 1 });
        pagesArray.push({ key: ellipsis, value: ellipsis });
        let counter =
          pages - maxNumberOfButtons + Math.floor(maxNumberOfButtons / 2) - 1;
        while (counter <= pages) {
          pagesArray.push({ key: counter, value: counter });
          counter += 1;
        }
      } else {
        // somewhere in the middle e.g. [1,'...',4,_5_,6,'...',19]
        pagesArray.push({ key: 1, value: 1 });
        pagesArray.push({ key: ellipsis + "1", value: ellipsis });
        let counter = page - Math.floor(maxNumberOfButtons / 2) + 3;
        const maxCounter = counter + Math.floor(maxNumberOfButtons / 2) + 1;
        while (counter < maxCounter) {
          pagesArray.push({ key: counter, value: counter });
          counter += 1;
        }
        pagesArray.push({ key: ellipsis + "2", value: ellipsis });
        pagesArray.push({ key: pages, value: pages });
      }
    }
    return (
      <div>
        {pagesArray.map(pageItem => {
          if (pageItem.value === ellipsis) {
            return (
              <Button
                variant="outlined"
                className={classes.button}
                key={pageItem.key.toString()}
              >
                {ellipsis}
              </Button>
            );
          } else {
            return (
              <Button
                variant="outlined"
                className={
                  page + 1 === pageItem.value
                    ? classes.selectedButton
                    : classes.button
                }
                onClick={() =>
                  typeof pageItem.value === "number"
                    ? changePage(pageItem.value - 1)
                    : "..."
                }
                key={pageItem.key.toString()}
              >
                {pageItem.value}
              </Button>
            );
          }
        })}
      </div>
    );
  };

  return (
    <Grid item sm={12}>
      <div className={classes.pageControlsWrapper}>
        <IconButton
          color="primary"
          onClick={handleBackButtonClick}
          disabled={page === 0}
          aria-label="previous page"
        >
          <KeyboardArrowLeft />
        </IconButton>
        {renderButtons()}
        <IconButton
          color="primary"
          onClick={handleNextButtonClick}
          disabled={page >= pages - 1}
          aria-label="next page"
        >
          <KeyboardArrowRight />
        </IconButton>
      </div>
    </Grid>
  );
};

export default AdhocProjectsTablePagination;
